import React, { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Table from '@components/common/Table/Table';

import { useQueryAssetProfileIncomingRequests } from '@hooks/assets/useQueryAssetProfileIncomingRequests';

import { assetProfileIncomingRequests } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setEndRentDate,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setStartRentDate,
} from '@slices/assetProfileIncomingRequests';

import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
} from '@interfaces/components/table';

import { getColumns } from './manageIncomingRequestsColumns';

const IncomingRequests = ({
	assetDbId,
}: { assetDbId?: string }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		startRentDate,
		endRentDate,
	} = useAppSelector(assetProfileIncomingRequests);

	const {
		incomingRequests,
		isLoadingIncomingRequests,
		countOfRecords,
	} = useQueryAssetProfileIncomingRequests({ assetDbId, isIncomingRequestsTabShown: true });

	const columns = useMemo(() => getColumns(navigate), [navigate]);

	const startDate = useMemo(() => startRentDate ? new Date(startRentDate) : null, [startRentDate]);
	const endDate = useMemo(() => endRentDate ? new Date(endRentDate) : null, [endRentDate]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			placeholder: 'Search by request ID',
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const onChangeStartRentDate = useCallback((value: number | null) => {
		dispatch(setStartRentDate({ startRentDate: value }));
	}, [dispatch]);

	const onChangeEndRentDate = useCallback((value: number | null) => {
		dispatch(setEndRentDate({ endRentDate: value }));
	}, [dispatch]);

	const dateFilters: Array<ITableDatepickerFilter> = useMemo(() => (
		[
			{
				name: 'startRentDate',
				label: 'Start Rent Date',
				handleChange: onChangeStartRentDate,
				selected: startDate,
			},
			{
				name: 'endRentDate',
				label: 'End Rent Date',
				handleChange: onChangeEndRentDate,
				selected: endDate,
			},
		]
	), [
		startDate,
		endDate,
		onChangeStartRentDate,
		onChangeEndRentDate,
	]);

	return (
		<div>
			<Table
				columns={columns}
				data={incomingRequests}
				dateFilters={dateFilters}
				isLoading={isLoadingIncomingRequests}
				isWithoutActions={false}
				isWithoutCheckbox
				isWithoutDeleteAction
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
			/>
		</div>
	);
};

export default memo(IncomingRequests);
